import React from "react";
import { Popper } from "@mui/material";
import { FolderIcon, FileIcon, CaretRight } from "@phosphor-icons/react";
import { Datum } from "./datastructure";
import {
  clearProductTimeout,
  setProductTimeout,
  type ProductTimer,
} from "@/utils/productTimers";

// Per-row hover state for the path tooltip. State lives in the row component
// (not the parent), so hovering between rows only re-renders the two rows
// involved — never the full tree.
//
// Previously the tree mounted one MUI <Tooltip> per row with the breadcrumb
// JSX eagerly built into the title prop; with 8k+ rows on large orgs (VU)
// that consumed hundreds of MB at idle. The Tooltip mount now happens lazily
// on hover via this hook.

const HOVER_OPEN_DELAY_MS = 100;

// A tap on a touch device synthesizes mouseenter with no mouseleave to
// follow, so hover chrome fires and then LINGERS over the rows (#11742,
// CD (c)). The tooltip is hover chrome — it stands down entirely where no
// hover-capable pointer exists.
const hoverCapable = (): boolean =>
  typeof window.matchMedia !== "function" ||
  window.matchMedia("(hover: hover)").matches;

export interface RowTooltipBind {
  onMouseEnter: () => void;
  onMouseLeave: () => void;
}

export function useRowPathTooltip(): { show: boolean; bind: RowTooltipBind } {
  const [show, setShow] = React.useState(false);
  const timerRef = React.useRef<ProductTimer | null>(null);

  const clear = () => {
    if (timerRef.current !== null) {
      clearProductTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const onMouseEnter = React.useCallback(() => {
    if (!hoverCapable()) return;
    clear();
    timerRef.current = setProductTimeout(
      () => setShow(true),
      HOVER_OPEN_DELAY_MS
    );
  }, []);

  const onMouseLeave = React.useCallback(() => {
    clear();
    setShow(false);
  }, []);

  React.useEffect(() => clear, []);

  return { show, bind: { onMouseEnter, onMouseLeave } };
}

export const PathTooltipPopper = ({
  anchor,
  datum,
  isFolder,
  driveIdToName,
}: {
  anchor: HTMLElement | null;
  datum: Datum;
  isFolder: boolean;
  driveIdToName?: Map<string, string>;
}) => {
  if (!anchor || datum.path.length === 0) return null;
  return (
    <Popper
      open
      anchorEl={anchor}
      placement="bottom-start"
      style={{ zIndex: 1500, pointerEvents: "none" }}
      modifiers={[{ name: "offset", options: { offset: [0, 4] } }]}
    >
      <div
        className="rounded-lg shadow-[0_10px_25px_-5px_rgba(0,0,0,0.3)]"
        style={{ backgroundColor: "#1f2937" }}
      >
        <PathTooltipContent
          datum={datum}
          driveIdToName={driveIdToName}
          isFolder={isFolder}
        />
      </div>
    </Popper>
  );
};

const PathTooltipContent = ({
  datum,
  driveIdToName,
  isFolder,
}: {
  datum: Datum;
  driveIdToName?: Map<string, string>;
  isFolder: boolean;
}) => {
  const path = datum.path;
  if (path.length === 0) return null;

  let driveName: string | undefined;
  if (datum.payload?.type === "folder" && datum.payload.driveName) {
    driveName = datum.payload.driveName;
  } else if (datum.payload?.driveId && driveIdToName) {
    driveName = driveIdToName.get(datum.payload.driveId);
  }

  const pathSegments = driveName ? [driveName, ...path.slice(1)] : [...path];

  return (
    <div className="flex items-center gap-1 py-1 px-2 max-w-[400px] flex-wrap">
      {pathSegments.map((segment, index) => {
        const isLast = index === pathSegments.length - 1;
        const showFileIcon = isLast && !isFolder;

        return (
          <React.Fragment key={index}>
            {showFileIcon ? (
              <FileIcon
                weight="fill"
                className="w-3.5 h-3.5 flex-shrink-0 text-blue-400"
              />
            ) : (
              <FolderIcon
                weight="fill"
                className={`w-3.5 h-3.5 flex-shrink-0 ${
                  isLast ? "text-yellow-400" : "text-gray-400"
                }`}
              />
            )}
            <span
              className={`text-[12px] ${
                isLast ? "text-white font-medium" : "text-gray-300"
              }`}
            >
              {segment}
            </span>
            {!isLast && (
              <CaretRight className="w-3 h-3 text-gray-500 flex-shrink-0 mx-0.5" />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};
